import { FC, memo, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { Box, Divider, Typography } from "@mui/material";
import { MiButton } from "../MiButton/MiButton";
import { MiIcon } from "../MiIcon/MiIcon";

export interface MiDrawerHeaderProps {
  title?: string;
  subtitle?: string | string[];
  leftIcon?: string;
  leftIconColor?: string;
  color?: string;
  removeMargin?: boolean;
  containerStyle?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  horizontalTitles?: boolean;
  rightAction?: React.ReactNode;
  rightIcon?: string | null;
  rightIconColor?: string;
  onRightIconClick?: () => void;
  onLeftIconClick?: () => void;
  stickyHeader?: boolean;
}

export const MiDrawerHeader: FC<MiDrawerHeaderProps> = memo(
  ({
    title = "",
    subtitle,
    leftIcon,
    leftIconColor,
    onLeftIconClick,
    rightIcon,
    rightIconColor,
    onRightIconClick,
    color,
    rightAction,
    headerStyle,
    containerStyle,
    stickyHeader = false,
    ...rest
  }) => {
    const { classes, theme } = useStyles({ stickyHeader });
    const subtitles = useMemo(() => {
      return (Array.isArray(subtitle) ? subtitle : [subtitle]).filter(Boolean);
    }, [subtitle]);

    return (
      <Box className={classes.container} style={containerStyle}>
        <Box className={classes.header} style={headerStyle} {...rest}>
          <Box className={classes.leftPart}>
            {leftIcon &&
              (onLeftIconClick ? (
                <MiButton
                  sx={{ padding: 1 }}
                  startIcon={{
                    path: leftIcon,
                    iconColor: leftIconColor || theme.palette.primary.main,
                  }}
                  onClick={onLeftIconClick}
                  data-testid="left-icon-button"
                />
              ) : (
                <MiIcon
                  path={leftIcon}
                  color={leftIconColor || theme.palette.primary.main}
                  onClick={onLeftIconClick}
                  data-testid="left-icon"
                />
              ))}
            {color && (
              <Box
                data-testid="header-color"
                className={classes.jobTypeColor}
                sx={{ backgroundColor: color }}
              />
            )}
            <Box className={classes.headerText}>
              <Box display="flex" gap={2}>
                <Typography className={classes.title}>{title}</Typography>
                {subtitles.map((sub) => (
                  <Box key={sub} display="flex" gap={2}>
                    <Divider orientation="vertical" />
                    <Typography className={classes.subtitle}>{sub}</Typography>
                  </Box>
                ))}
              </Box>
              {rightIcon &&
                (onRightIconClick ? (
                  <MiButton
                    startIcon={{
                      path: rightIcon,
                      iconColor:
                        rightIconColor || (theme.palette.grey as any).main,
                      iconSize: 0.9,
                    }}
                    onClick={onRightIconClick}
                    data-testid="right-icon-button"
                    style={{ padding: theme.spacing(1) }}
                  />
                ) : (
                  <MiIcon
                    path={rightIcon}
                    size={0.8}
                    color={rightIconColor || (theme.palette.grey as any).main}
                    onClick={onRightIconClick}
                    data-testid="right-icon"
                  />
                ))}
            </Box>
          </Box>
          {rightAction}
        </Box>
      </Box>
    );
  }
);

const useStyles = makeStyles<{ stickyHeader?: boolean }>()(
  (theme, { stickyHeader }) => ({
    container: {
      display: "flex",
      backgroundColor: theme.palette.grey.mediumLight,
      borderBottom: `1px solid ${theme.palette.grey.accent}`,
      ...(stickyHeader && {
        position: "sticky",
        top: 0,
        zIndex: theme.zIndex.appBar,
      }),
      height: 36,
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: theme.spacing(1, 4),
      position: "sticky",
      top: 0,
      zIndex: 2,
      width: "100%",
    },
    leftPart: {
      display: "flex",
      flexDirection: "row",
      gap: 8,
      alignItems: "center",
      width: "100%",
      flex: 1,
    },
    title: {
      fontWeight: (theme as any).fontWeight.semiBold,
      fontSize: theme.typography.pxToRem(14),
    },
    subtitle: {
      color: theme.palette.grey.medium,
      fontSize: theme.typography.pxToRem(14),
    },
    jobTypeColor: {
      width: 15,
      height: 15,
    },
    divider: {
      marginBottom: 8,
    },
    headerText: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
    },
    flexSpaceBetween: {
      display: "flex",
      alignItems: "center",
      width: "100%",
      gap: theme.spacing(2),
    },
  })
);
